/* =============================================================
   Vegababe — interactive layer (custom elements, vanilla JS).
   Loaded by layout/theme.liquid (defer).
   No window-storage (only cookies) for cart/customer state — Dawn keeps it
   server-side and publishes `cart:updated` on the document.
   ============================================================= */

(function () {
  'use strict';

  // -----------------------------------------------------------
  // VegababeHeader — glass pill scroll behaviour, mobile drawer,
  //                  mega-menu hover/keyboard.
  // -----------------------------------------------------------
  class VegababeHeader extends HTMLElement {
    connectedCallback() {
      this._bar = this.querySelector('[data-vb-header-bar]');
      this._burger = this.querySelector('[data-vb-burger]');
      this._mobileNav = this.querySelector('vegababe-mobile-nav');
      this._mega = this.querySelector('vegababe-mega-menu');
      this._shopTrigger = this.querySelector('[data-vb-shop-trigger]');
      this._bgStart = parseFloat(this.dataset.bgStart || '0.35');
      this._bgEnd = parseFloat(this.dataset.bgEnd || '0.90');
      this._ticking = false;
      this._lastQ = -1;
      this._onScroll = this._onScroll.bind(this);
      this._onBurger = this._onBurger.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onMegaEnter = this._onMegaEnter.bind(this);
      this._onMegaLeave = this._onMegaLeave.bind(this);
      this._onMegaKey = this._onMegaKey.bind(this);

      this._applyScroll();
      window.addEventListener('scroll', this._onScroll, { passive: true });
      if (this._burger) this._burger.addEventListener('click', this._onBurger);
      window.addEventListener('keydown', this._onKey);
      if (this._mega) {
        this._mega.addEventListener('mouseenter', this._onMegaEnter);
        this._mega.addEventListener('mouseleave', this._onMegaLeave);
        this._mega.addEventListener('keydown', this._onMegaKey);
      }
    }
    disconnectedCallback() {
      window.removeEventListener('scroll', this._onScroll);
      if (this._burger) this._burger.removeEventListener('click', this._onBurger);
      window.removeEventListener('keydown', this._onKey);
      if (this._mega) {
        this._mega.removeEventListener('mouseenter', this._onMegaEnter);
        this._mega.removeEventListener('mouseleave', this._onMegaLeave);
        this._mega.removeEventListener('keydown', this._onMegaKey);
      }
    }
    _onScroll() {
      if (this._ticking) return;
      this._ticking = true;
      requestAnimationFrame(this._applyScroll.bind(this));
    }
    _applyScroll() {
      this._ticking = false;
      if (!this._bar) return;
      const MAX = 200;
      const raw = Math.min(1, Math.max(0, window.scrollY / MAX));
      const q = Math.round(raw * 50) / 50;
      if (q === this._lastQ) return;
      this._lastQ = q;
      const bg = this._bgStart + q * (this._bgEnd - this._bgStart);
      this._bar.style.setProperty('--hdr-bg', bg.toFixed(3));
      this._bar.style.setProperty('--hdr-blur', (6 + q * 14).toFixed(1) + 'px');
      this._bar.style.setProperty('--hdr-border', (0.15 - q * 0.05).toFixed(3));
      this._bar.style.setProperty('--hdr-shadow-a', (0.15 + q * 0.10).toFixed(3));
    }
    _onBurger() {
      if (!this._mobileNav) return;
      const open = this._mobileNav.dataset.open === 'true';
      this._mobileNav.dataset.open = open ? 'false' : 'true';
      this._burger.setAttribute('aria-expanded', String(!open));
    }
    _onKey(e) {
      if (e.key === 'Escape') {
        if (this._mobileNav && this._mobileNav.dataset.open === 'true') {
          this._mobileNav.dataset.open = 'false';
          if (this._burger) this._burger.setAttribute('aria-expanded', 'false');
        }
        if (this._mega && this._mega.dataset.open === 'true') {
          this._mega.dataset.open = 'false';
        }
      }
    }
    _onMegaEnter() { if (this._mega) this._mega.dataset.open = 'true'; }
    _onMegaLeave() { if (this._mega) this._mega.dataset.open = 'false'; }
    _onMegaKey(e) {
      // Keyboard nav between mega-menu items
      const items = this._mega ? Array.from(this._mega.querySelectorAll('a')) : [];
      if (!items.length) return;
      const current = document.activeElement;
      const idx = items.indexOf(current);
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        items[(idx + 1 + items.length) % items.length].focus();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        items[(idx - 1 + items.length) % items.length].focus();
      }
    }
  }
  customElements.define('vegababe-header', VegababeHeader);

  // -----------------------------------------------------------
  // VegababeMobileNav — thin proxy that just toggles a data attr
  //                   on its child <vegababe-mobile-nav>.
  // -----------------------------------------------------------
  // (Defined in <vegababe-header> logic; this stub is a no-op.)
  // (We could split into another class if needed.)

  // -----------------------------------------------------------
  // VegababeMegaMenu — same as above; the parent header handles
  //                 open/close. Stub for explicit registration.
  // -----------------------------------------------------------
  class VegababeMegaMenu extends HTMLElement {
    connectedCallback() { /* parent manages state */ }
  }
  customElements.define('vegababe-mega-menu', VegababeMegaMenu);

  class VegababeMobileNav extends HTMLElement {
    connectedCallback() { /* parent manages state */ }
  }
  customElements.define('vegababe-mobile-nav', VegababeMobileNav);

  // -----------------------------------------------------------
  // VegababeMacroBars — IntersectionObserver-driven bar animation.
  // -----------------------------------------------------------
  class VegababeMacroBars extends HTMLElement {
    connectedCallback() {
      this._onIntersect = this._onIntersect.bind(this);
      this._obs = new IntersectionObserver(this._onIntersect, { threshold: 0.4 });
      this._obs.observe(this);
    }
    disconnectedCallback() {
      if (this._obs) this._obs.disconnect();
    }
    _onIntersect(entries) {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          this.dataset.animate = 'true';
          if (this._obs) this._obs.disconnect();
          break;
        }
      }
    }
  }
  customElements.define('vegababe-macro-bars', VegababeMacroBars);

  // -----------------------------------------------------------
  // VegababeNewsletterPopup — 8s delay, cookie-based "seen" state.
  // -----------------------------------------------------------
  const VB_COOKIE_NAME = 'vb_newsletter_popup_seen';
  const VB_COOKIE_DAYS = 30;

  function vbSetCookie(name, value, days) {
    const d = new Date();
    d.setTime(d.getTime() + (days * 86400000));
    document.cookie = name + '=' + value + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
  }
  function vbGetCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      const c = ca[i].trim();
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  class VegababeNewsletterPopup extends HTMLElement {
    connectedCallback() {
      if (vbGetCookie(VB_COOKIE_NAME)) return;
      const delay = parseInt(this.dataset.delay || '8000', 10);
      this._t = setTimeout(() => this._open(), delay);
      this._onClose = this._onClose.bind(this);
      this._onKey = this._onKey.bind(this);
      this._onSubmit = this._onSubmit.bind(this);
      this.addEventListener('click', this._onClickOutside.bind(this));
      this.querySelector('[data-vb-popup-close]')?.addEventListener('click', this._onClose);
      this.querySelector('[data-vb-popup-form]')?.addEventListener('submit', this._onSubmit);
      window.addEventListener('keydown', this._onKey);
    }
    disconnectedCallback() {
      if (this._t) clearTimeout(this._t);
      window.removeEventListener('keydown', this._onKey);
    }
    _open() {
      this.dataset.open = 'true';
      // Focus the close button for keyboard users
      setTimeout(() => this.querySelector('[data-vb-popup-close]')?.focus(), 100);
    }
    _close() {
      this.dataset.open = 'false';
      vbSetCookie(VB_COOKIE_NAME, '1', VB_COOKIE_DAYS);
    }
    _onClose() { this._close(); }
    _onKey(e) { if (e.key === 'Escape' && this.dataset.open === 'true') this._close(); }
    _onClickOutside(e) {
      if (e.target === this) this._close();
    }
    _onSubmit(e) {
      e.preventDefault();
      const input = this.querySelector('input[type="email"]');
      if (!input || !input.value.includes('@')) return;
      this._close();
    }
  }
  customElements.define('vegababe-newsletter-popup', VegababeNewsletterPopup);

  // -----------------------------------------------------------
  // Bundle selector (in product page) — updates qty input.
  // -----------------------------------------------------------
  function initBundleSelectors() {
    document.querySelectorAll('[data-vb-bundle-selector]').forEach((root) => {
      const opts = root.querySelectorAll('[data-vb-bundle]');
      const qtyInput = document.querySelector('quantity-input input[name="quantity"], input[name="quantity"][type="number"]');
      const updateQty = (q) => {
        if (qtyInput) {
          qtyInput.value = q;
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      };
      const setActive = (opt) => {
        opts.forEach((o) => {
          o.classList.toggle('vb-bundle-option--active', o === opt);
          const radio = o.querySelector('input[type="radio"]');
          if (radio) radio.checked = o === opt;
        });
      };
      opts.forEach((opt) => {
        opt.addEventListener('click', () => {
          setActive(opt);
          const q = parseInt(opt.dataset.qty || '1', 10);
          updateQty(q);
        });
      });
      // Initial qty
      const active = root.querySelector('.vb-bundle-option--active') || opts[0];
      if (active) updateQty(parseInt(active.dataset.qty || '1', 10));
    });
  }
  // Run on load and after each shopify:section:load
  document.addEventListener('DOMContentLoaded', initBundleSelectors);
  document.addEventListener('shopify:section:load', initBundleSelectors);

  // -----------------------------------------------------------
  // Quantity stepper (increment/decrement)
  // -----------------------------------------------------------
  function initQtySteppers() {
    document.querySelectorAll('[data-vb-qty-stepper]').forEach((root) => {
      if (root.dataset.vbInited) return;
      root.dataset.vbInited = '1';
      const input = root.querySelector('[data-vb-qty-input]');
      const minus = root.querySelector('[data-vb-qty-minus]');
      const plus = root.querySelector('[data-vb-qty-plus]');
      if (!input) return;
      const update = (delta) => {
        const min = parseInt(input.getAttribute('min') || '0', 10);
        const max = parseInt(input.getAttribute('max') || '99', 10);
        const next = Math.max(min, Math.min(max, (parseInt(input.value, 10) || 0) + delta));
        input.value = String(next);
        input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      if (minus) minus.addEventListener('click', () => update(-1));
      if (plus) plus.addEventListener('click', () => update(1));
    });
  }
  document.addEventListener('DOMContentLoaded', initQtySteppers);
  document.addEventListener('shopify:section:load', initQtySteppers);

  // -----------------------------------------------------------
  // Cart progress (free-shipping + bundle) — recompute on cart update.
  // -----------------------------------------------------------
  function updateShippingProgress() {
    const settings = (window.Vegababe && window.Vegababe.settings) || {};
    const threshold = settings.freeShippingThreshold || 4900;
    const cart = window.cart || {};
    const total = cart.total_price || 0;
    document.querySelectorAll('.vb-cart-progress [data-vb-shipping-bar]').forEach((bar) => {
      const fill = bar.querySelector('.vb-progress__fill');
      const barThreshold = parseInt(bar.getAttribute('data-vb-shipping-threshold'), 10);
      const useThreshold = barThreshold > 0 ? barThreshold : threshold;
      if (fill) fill.style.setProperty('--vb-progress-pct', Math.min(100, (total / useThreshold) * 100) + '%');
    });
    document.querySelectorAll('.vb-cart-progress [data-vb-bundle-bar]').forEach((bar) => {
      const tiers = (settings.bundleTiers || '3:10,6:15').split(',').map((s) => s.split(':').map(Number));
      const count = cart.item_count || 0;
      const fill = bar.querySelector('.vb-progress__fill');
      const next = tiers.find((t) => count < t[0]);
      if (next && fill) {
        fill.style.setProperty('--vb-progress-pct', Math.min(100, (count / next[0]) * 100) + '%');
      }
    });
  }
  document.addEventListener('cart:updated', updateShippingProgress);
  document.addEventListener('DOMContentLoaded', updateShippingProgress);

  // -----------------------------------------------------------
  // Re-init on Shopify section load/unload
  // -----------------------------------------------------------
  document.addEventListener('shopify:section:unload', function (e) {
    // Cleanup happens automatically via custom element disconnectedCallback.
  });

  // -----------------------------------------------------------
  // Add-to-cart: open the cart drawer after success
  // -----------------------------------------------------------
  document.addEventListener('DOMContentLoaded', function () {
    // Intercept form submissions to /cart/add.js and open the drawer on success
    document.querySelectorAll('form[action*="/cart/add"]').forEach(function (form) {
      form.addEventListener('submit', function (ev) {
        // Standard form submission will reload the page in the default Dawn setup
        // if the form has method="post" without enctype. We use enctype="multipart/form-data"
        // so the submission goes to the cart add endpoint and returns the cart JSON.
        // To keep things simple AND match Dawn, we let the form submit normally.
        // Dawn's cart-drawer will see the cart:updated event.
        // We also auto-open the cart drawer after a short delay.
        setTimeout(function () {
          const drawer = document.querySelector('cart-drawer');
          if (drawer && typeof drawer.open === 'function') drawer.open();
        }, 350);
      });
    });
  });
})();
